import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Input, Select, ContentLoader, ErrorSummary, BackButton } from '@/components/common';
import { applyServerErrors } from '@/utils/formErrors';
import { phoneSchema, digitsOnlyOnChange, nameOnChange, optionalEmailSchema, allowUnchangedEmails } from '@/utils/validation';
import { useUser, useCreateUser, useUpdateUser } from '@/hooks/useUsers';
import { useBrokers } from '@/hooks/useBrokers';
import { useInsurersLookup } from '@/hooks/useInsurers';
import { useCountryLookup } from '@/hooks/useGeography';
import { useAuthStore } from '@/store';
import type { CreatedUser, IssuedCredentials } from '@/services/users';

const userCreateSchema = z
  .object({
    first_name: z.string().min(1, 'First name is required'),
    last_name: z.string().min(1, 'Last name is required'),
    email: optionalEmailSchema,
    phone: phoneSchema,
    // Board #180: optional. Blank means the SERVER issues a one-time password
    // and returns it once in the 201 — the admin no longer has to invent one
    // and find a channel to pass it on. A typed password still has to be a
    // real one.
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .optional()
      .or(z.literal('')),
    role: z.string().min(1, 'Role is required'),
    tenant_type: z.enum(['broker', 'insurer', 'platform']).optional(),
    tenant_id: z.string().optional(),
    country: z.string().optional(),
  })
  // A Country Admin with no country is not a smaller admin -- they are a
  // BIGGER one. Downstream scoping reads
  // `if user.role == "country_admin" and user.country_id`, so a NULL country
  // makes the condition false and the query runs unscoped: that admin sees
  // every country's data. This form had no country field at all, and it is the
  // only route the product offers for creating the role. The server refuses it
  // too (AdminUserCreateSerializer.validate); this is so nobody has to find
  // that out from a 400.
  .superRefine((data, ctx) => {
    if (data.role === 'country_admin' && !data.country) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['country'],
        message: 'Country is required for a Country Admin',
      });
    }
  });

/** Board #180: the one-time password screen, shown INSTEAD of navigating away.
 *  There is exactly one moment this value exists; leaving the page loses it. */
function IssuedCredentialsCard({
  credentials,
  onDone,
  copied,
  onCopy,
}: {
  credentials: IssuedCredentials;
  onDone: () => void;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="max-w-xl">
      <PageHeader title="User created" breadcrumbs={[{ label: 'Users', href: '/users' }]} />
      <Card>
        <CardHeader
          title="One-time password"
          subtitle="This is the only time it can be shown. Copy it before you leave this page."
        />
        <dl className="space-y-3 text-sm">
          {credentials.email && (
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Email</dt>
              <dd className="font-medium text-gray-900">{credentials.email}</dd>
            </div>
          )}
          {credentials.phone && (
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Phone</dt>
              <dd className="font-medium text-gray-900">{credentials.phone}</dd>
            </div>
          )}
          <div className="flex justify-between gap-4 items-center">
            <dt className="text-gray-500">Password</dt>
            <dd className="font-mono text-gray-900" data-testid="issued-password">
              {credentials.password}
            </dd>
          </div>
        </dl>
        <p className="mt-4 text-sm text-gray-500">{credentials.message}</p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" type="button" onClick={onCopy}>
            {copied ? 'Copied' : 'Copy password'}
          </Button>
          <Button type="button" onClick={onDone}>
            Done
          </Button>
        </div>
      </Card>
    </div>
  );
}

const userUpdateSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: optionalEmailSchema,
  phone: phoneSchema,
  role: z.string().min(1, 'Role is required'),
});

type UserCreateFormData = z.infer<typeof userCreateSchema>;
type UserUpdateFormData = z.infer<typeof userUpdateSchema>;

const allRoleOptions = [
  { value: 'broker_admin', label: 'Broker Admin' },
  { value: 'broker_employee', label: 'Broker Employee' },
  { value: 'posp', label: 'POSP' },
  { value: 'super_posp', label: 'Super POSP' },
  { value: 'insurer_admin', label: 'Insurer Admin' },
  { value: 'insurer_employee', label: 'Insurer Employee' },
  { value: 'country_admin', label: 'Country Admin' },
];

const rolesByUserRole: Record<string, string[]> = {
  broker_admin: ['broker_employee', 'posp'],
  // V-01: no insurer_employee -- an insurer's Managers/Staff are added by that
  // insurer's own admin (Team screen); the server refuses it from here.
  country_admin: ['broker_admin', 'broker_employee', 'posp', 'super_posp', 'insurer_admin'],
  super_admin: ['country_admin', 'broker_admin', 'broker_employee', 'posp', 'super_posp', 'insurer_admin', 'insurer_employee'],
};

const tenantTypeOptions = [
  { value: 'broker', label: 'Broker' },
  { value: 'insurer', label: 'Insurer' },
  { value: 'platform', label: 'Platform' },
];

export function UserFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(id);
  /**
   * The broker this form was opened FROM, if any (Broker Detail › Broker Admin
   * › Edit admin passes `?broker=<id>`).
   *
   * Every way out of this page used to be hard-coded to `/users`, and `/users`
   * is routed for `super_admin` and `broker_admin` only — so a country admin,
   * the role whose job this is, pressed Back and got Access Denied. With the
   * context, they go back where they came from, on the tab they came from.
   *
   * A broker ID, deliberately, not a `returnTo` URL: it can only ever produce
   * a link into /brokers/<id>, so it cannot be used to bounce somebody
   * somewhere else.
   */
  const brokerContext = searchParams.get('broker');
  const brokerReturn = brokerContext
    ? `/brokers/${encodeURIComponent(brokerContext)}?tab=admin`
    : null;
  /** Where Back, Cancel and a successful save all go. */
  const exitTo = brokerReturn ?? (isEditing && id ? `/users/${id}` : '/users');
  const { user: currentUser } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: user, isLoading: userLoading } = useUser(id!);
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();

  const roleOptions = useMemo(() => {
    // Opened as "edit this broker's admin": the only role on offer is Broker
    // Admin. The other six were reachable here, and picking one of them would
    // quietly take the broker's only admin login away from it.
    if (brokerContext) {
      // ...plus whatever role the edited login ACTUALLY holds. Nothing forces a
      // broker's admin to be a `broker_admin`: `map_user` writes
      // BrokerUser(role='ADMIN') for any user_id with no check on User.role,
      // and the assign-admin picker searches every user. Dropping that user's
      // own role left the <select> with no matching <option> (selectedIndex -1,
      // value ''), so zod's `role: min(1)` refused the form and a phone-number
      // correction could not be saved at all -- the only way out being to pick
      // Broker Admin and perform the very demotion this narrowing prevents.
      return allRoleOptions.filter(
        (opt) => opt.value === 'broker_admin' || opt.value === user?.role
      );
    }
    const allowed = rolesByUserRole[currentUser?.role || ''] || [];
    return allRoleOptions.filter((opt) => allowed.includes(opt.value));
  }, [currentUser?.role, brokerContext, user?.role]);

  // Broker admins don't need to pick tenant type - auto-assigned by backend
  const isBrokerAdmin = currentUser?.role === 'broker_admin';
  // Country admins create users across multiple tenants, so they must pick the
  // specific organization the new user belongs to (backend does not auto-resolve
  // tenant_id for country_admin).
  const isCountryAdmin = currentUser?.role === 'country_admin';

  const [serverMessages, setServerMessages] = useState<string[]>([]);
  // Board #180: the server-issued password, shown once after a successful
  // create. Held in state rather than passed on through navigation because
  // this is the only moment it exists — nothing can fetch it back.
  const [issued, setIssued] = useState<{ user: CreatedUser; credentials: IssuedCredentials } | null>(
    null
  );
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors },
  } = useForm<UserCreateFormData | UserUpdateFormData>({
    resolver: allowUnchangedEmails(
      zodResolver(isEditing ? userUpdateSchema : userCreateSchema),
      () => ({ email: isEditing ? user?.email : undefined }),
    ),
    defaultValues: {
      role: roleOptions[0]?.value || 'broker_employee',
    },
  });

  // Country admins must select the organization the new user belongs to. The
  // available organizations depend on the chosen tenant type.
  const selectedTenantType = watch('tenant_type' as keyof UserCreateFormData);
  const showTenantSelector = !isEditing && isCountryAdmin;

  // Country is asked for exactly when it is load-bearing: a new Country Admin.
  // Every other role's country is inherited server-side from the creating admin
  // (or resolved from the broker), which is the behaviour that was already
  // correct and is left alone.
  const selectedRole = watch('role');
  const showCountrySelector = !isEditing && selectedRole === 'country_admin';
  const { data: countryLookup } = useCountryLookup();
  // The empty entry is a real, SELECTABLE option rather than `Select`'s
  // `placeholder` prop, which renders a *disabled* first option -- and a
  // disabled first option is skipped when the browser picks the initial
  // selection, so the field would silently arrive pre-set to whichever country
  // happens to sort first. Picking a country admin's country by accident is the
  // failure this whole field exists to prevent.
  const countryOptions = useMemo(
    () => [
      { value: '', label: 'Select country...' },
      ...(countryLookup ?? []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [countryLookup]
  );

  const { data: brokersData } = useBrokers(1, undefined);
  const { data: insurersLookup } = useInsurersLookup();

  const tenantOptions = useMemo(() => {
    if (selectedTenantType === 'broker') {
      return (brokersData?.results ?? []).map((b) => ({ value: b.id, label: b.name }));
    }
    if (selectedTenantType === 'insurer') {
      return (insurersLookup ?? []).map((i) => ({ value: i.id, label: i.name }));
    }
    return [];
  }, [selectedTenantType, brokersData, insurersLookup]);

  useEffect(() => {
    if (user) {
      reset({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email || '',
        phone: user.phone || '',
        role: user.role,
      });
    }
  }, [user, reset]);

  const onSubmit = (data: UserCreateFormData | UserUpdateFormData) => {
    setServerMessages([]);

    const cleanedData = {
      ...data,
      email: data.email || undefined,
      // #180: an empty box is "generate one", not a password of "".
      ...('password' in data ? { password: data.password || undefined } : {}),
      // Drop empty organization selection so the backend doesn't receive "".
      ...('tenant_id' in data ? { tenant_id: data.tenant_id || undefined } : {}),
      // Same for country: "" is not a country, and sending it turns the
      // serializer's clear "this field is required" into a type error.
      ...('country' in data ? { country: data.country || undefined } : {}),
    };

    const onError = (error: unknown) => {
      setServerMessages(applyServerErrors(error, setError));
    };

    if (isEditing) {
      // `role` is read-only server-side on this route; sending it produced a
      // 200 that changed nothing. Drop it rather than imply it was saved.
      delete (cleanedData as { role?: string }).role;
      updateMutation.mutate(
        { id: id!, data: cleanedData as Parameters<typeof updateMutation.mutate>[0]['data'] },
        { onSuccess: () => navigate(exitTo), onError }
      );
    } else {
      createMutation.mutate(cleanedData as Parameters<typeof createMutation.mutate>[0], {
        onSuccess: (newUser) => {
          // FIX 7: creating a posp/super_posp user provisions a POSP row
          // server-side — refresh POSP lookups so dropdowns update without reload.
          queryClient.invalidateQueries({ queryKey: ['posps'] });
          // #180: if the server issued the password, STOP here and show it.
          // Navigating away would lose it for good — it is hashed on the row
          // and no screen can ever display it again.
          if (newUser.credentials) {
            setIssued({ user: newUser, credentials: newUser.credentials });
            return;
          }
          navigate(`/users/${newUser.id}`);
        },
        onError,
      });
    }
  };

  if (isEditing && userLoading) {
    return <ContentLoader />;
  }

  if (issued) {
    return (
      <IssuedCredentialsCard
        credentials={issued.credentials}
        onDone={() => navigate(`/users/${issued.user.id}`)}
        copied={copied}
        onCopy={() => {
          navigator.clipboard?.writeText(issued.credentials.password);
          setCopied(true);
        }}
      />
    );
  }

  return (
    <div>
      <div className="mb-4">
        <BackButton to={exitTo} />
      </div>
      <PageHeader
        title={isEditing ? 'Edit User' : 'Add User'}
        breadcrumbs={
          (brokerReturn
            ? [
                { label: 'Brokers', href: '/brokers' },
                { label: 'Broker Admin', href: brokerReturn },
                { label: isEditing ? 'Edit' : 'New' },
              ]
            : [
                { label: 'Users', href: '/users' },
                isEditing && user
                  ? { label: user.full_name, href: `/users/${id}` }
                  : null,
                { label: isEditing ? 'Edit' : 'New' },
              ]
          ).filter(Boolean) as { label: string; href?: string }[]
        }
        actions={
          <Link to={exitTo}>
            <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          <ErrorSummary messages={serverMessages} />

          {/* Personal Information */}
          <Card>
            <CardHeader title="Personal Information" subtitle="User's basic details" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Input
                label="First Name"
                error={errors.first_name?.message}
                required
                {...register('first_name', { onChange: nameOnChange })}
              />
              <Input
                label="Last Name"
                error={errors.last_name?.message}
                required
                {...register('last_name', { onChange: nameOnChange })}
              />
              <Input
                label="Phone Number"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="+91 99999 99999"
                error={errors.phone?.message}
                required
                {...register('phone', { onChange: digitsOnlyOnChange })}
              />
              <Input
                label="Email Address"
                type="email"
                placeholder="user@example.com"
                error={errors.email?.message}
                {...register('email')}
              />
            </div>
          </Card>

          {/* Access & Role */}
          <Card>
            <CardHeader title="Access & Role" subtitle="User permissions and access level" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* The server keeps `role` read-only on this route
                  (UserSerializer.Meta.read_only_fields), so an edit that
                  changed it used to return 200 and navigate away having
                  changed nothing -- the platform's signature "success with
                  nothing behind it". Until a governed set-role action exists
                  (like set-status), the edit form says so instead of
                  pretending. Creation still chooses the role freely. */}
              {isEditing ? (
                <div>
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role
                  </span>
                  <p className="text-sm text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2 bg-gray-50 dark:bg-gray-900/40">
                    {roleOptions.find((o) => o.value === user?.role)?.label || user?.role || '—'}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    A role cannot be changed after the login is created. Create a new login with the
                    role you need, and deactivate this one.
                  </p>
                </div>
              ) : (
                <Select
                  label="Role"
                  options={roleOptions}
                  error={errors.role?.message}
                  required
                  {...register('role')}
                />
              )}
              {!isEditing && !isBrokerAdmin && (
                <Select
                  label="Tenant Type"
                  options={tenantTypeOptions}
                  placeholder="Select..."
                  {...register('tenant_type' as keyof UserCreateFormData)}
                />
              )}
              {showTenantSelector && (selectedTenantType === 'broker' || selectedTenantType === 'insurer') && (
                <Select
                  label="Organization"
                  options={tenantOptions}
                  placeholder="Select organization..."
                  {...register('tenant_id' as keyof UserCreateFormData)}
                />
              )}
              {/* Deliberately no `required` prop: it sets the native
                  attribute, and the browser then blocks submit with its own
                  generic tooltip before react-hook-form runs -- so the zod
                  message, which is the one that explains WHY a country admin
                  needs a country, would never be seen. */}
              {showCountrySelector && (
                <Select
                  label="Country"
                  options={countryOptions}
                  error={
                    'country' in errors
                      ? (errors as { country?: { message?: string } }).country?.message
                      : undefined
                  }
                  helperText="Required - a Country Admin's whole scope is their country."
                  {...register('country' as keyof UserCreateFormData)}
                />
              )}
            </div>
          </Card>

          {/* Password (Create only) */}
          {!isEditing && (
            <Card>
              <CardHeader title="Security" subtitle="Set the initial password" />
              <div className="max-w-md">
                {/* No `required`: #180 makes a blank box mean "let the server
                    issue one", and the native attribute would block submit
                    before that could happen. */}
                <Input
                  label="Password"
                  type="password"
                  error={'password' in errors ? errors.password?.message : undefined}
                  {...register('password' as keyof UserCreateFormData)}
                />
                <p className="mt-2 text-sm text-gray-500">
                  Leave blank and a one-time password will be generated and shown to you once.
                  If you set one yourself, use 8 characters or more.
                </p>
              </div>
            </Card>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Link to={exitTo}>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {isEditing ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
